import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import type { GoalOverviewSubType } from "Infrastructure/ApiZfp/Schema";
import {
  MAX_INFLATION_RATE,
  MAX_LOAN_DURATION,
  MAX_LOAN_INTEREST_RATE,
  MIN_LOAN_DURATION,
} from "Infrastructure/Constants/financialConstants";
import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { Resources } from "Infrastructure/Translations/Resources";
import { addMonths, format, startOfMonth } from "date-fns";
import type { ObjPathProxy } from "ts-object-path";
import { type ObjectSchema, bool, date, mixed, object, string } from "yup";
import { number } from "../Utils/schemaUtils";

export type HousingPropertyGoalFormType = {
  readonly name: string;
  readonly goalSubType: GoalOverviewSubType;
  readonly targetDate: Date;
  readonly currentSavingsAmount: number;
  readonly expectedPriceAmount: number;
  readonly downpaymentAmount: number;
  readonly loanAmount: number;
  readonly loanToValue: number;
  readonly loanInterestRate: number;
  readonly loanDuration: number;
  readonly interestRate: number;
  readonly inflationRate: number;
  readonly feeRate: number;
  readonly investCurrentSavings: boolean;
  readonly referenceStartDate: Date;
  readonly includeInflation?: boolean;
  readonly includeEntryFee?: boolean;
  readonly note?: string;
  readonly priority?: boolean;
};

export const minDate = startOfMonth(addMonths(new Date(), 1));

export const getHousingPropertyFormSchema: (
  t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string,
) => ObjectSchema<HousingPropertyGoalFormType> = (t) => {
  return object({
    name: string().required(t(Resources.Validation.Required)),
    goalSubType: mixed<GoalOverviewSubType>().required(t(Resources.Validation.Required)),
    targetDate: date()
      .required(t(Resources.Validation.Required))
      .min(minDate, t(Resources.Validation.MustBeOlderThanDate, { date: format(minDate, "dd.MM.yyyy") })),
    referenceStartDate: date().required(t(Resources.Validation.Required)),
    currentSavingsAmount: number().required(t(Resources.Validation.Required)),
    expectedPriceAmount: number().required(t(Resources.Validation.Required)),
    downpaymentAmount: number().required(t(Resources.Validation.Required)),
    loanAmount: number().required(t(Resources.Validation.Required)),
    loanToValue: number()
      .max(100, t(Resources.Validation.Number.MaxShort, { count: 100 }))
      .required(t(Resources.Validation.Required)),
    loanInterestRate: number()
      .max(MAX_LOAN_INTEREST_RATE, t(Resources.Validation.Number.MaxShort, { count: MAX_LOAN_INTEREST_RATE }))
      .required(t(Resources.Validation.Required)),
    loanDuration: number()
      .min(MIN_LOAN_DURATION, t(Resources.Validation.Number.MinShort, { count: MIN_LOAN_DURATION }))
      .max(MAX_LOAN_DURATION, t(Resources.Validation.Number.MaxShort, { count: MAX_LOAN_DURATION }))
      .required(t(Resources.Validation.Required)),
    feeRate: number().required(t(Resources.Validation.Required)),
    interestRate: number().required(t(Resources.Validation.Required)),
    inflationRate: number()
      .required(t(Resources.Validation.Required))
      .max(MAX_INFLATION_RATE, t(Resources.Validation.Number.MaxShort, { count: MAX_INFLATION_RATE })),
    investCurrentSavings: bool().required(t(Resources.Validation.Required)),
    includeInflation: bool().required(t(Resources.Validation.Required)),
    includeEntryFee: bool().required(t(Resources.Validation.Required)),
    note: string().max(NOTE_MAX_LENGTH, t(Resources.Validation.String.Max, { count: NOTE_MAX_LENGTH })),
    priority: bool(),
  }).defined();
};
